import { listCollection } from "../lib/content.server.js";
import { breadcrumbJsonLd, buildMeta } from "../lib/seo.js";
import { SITE } from "../lib/site.js";
import Breadcrumbs from "../components/Breadcrumbs/Breadcrumbs.jsx";
import PersonCard from "../components/PersonCard/PersonCard.jsx";
import SectionHeader from "../components/SectionHeader/SectionHeader.jsx";
import CTABand from "../components/CTABand/CTABand.jsx";
import { surfaceFor } from "../lib/surface.js";
import styles from "./team.module.css";

const PAGE = {
  title: "Our Team",
  description:
    "Since 2005, the people of RDV Systems have been advancing 3D visualization software to improve infrastructure, for existing and new roads alike.",
  path: "/about/team",
};
const CRUMBS = [
  { name: "Home", path: "/" },
  { name: "About", path: "/about" },
  { name: "Team", path: PAGE.path },
];

export function meta() {
  return buildMeta({ ...PAGE, jsonLd: [breadcrumbJsonLd(CRUMBS)] });
}

export async function loader() {
  const people = listCollection("team").sort(
    (a, b) => (a.order || 99) - (b.order || 99) || a.name.localeCompare(b.name)
  );
  return { people };
}

// The one switch for this page's light sections: these sit on the light "plan sheet"
// surface from app/styles/surfaces.css. Empty the list to put the whole page back on the
// dark surface; nothing else needs to change.
const LIGHT_SECTIONS = ["sales", "board"];
const surface = surfaceFor(LIGHT_SECTIONS);

// In the order a visitor weighs them: who runs the company, who they will work with, who
// builds it, then the board. `size` is how much of each person the group shows.
const GROUPS = [
  { id: "leadership", title: "Leadership", size: "lead" },
  {
    id: "sales",
    title: "Your team in the US",
    aside:
      "Business development, project delivery and client success for DOTs and their consultants.",
    link: { to: "/contact", label: "Contact the team" },
    size: "member",
  },
  {
    id: "development",
    title: "The people who build it",
    aside: "Software, AI, 3D modeling and operations: the pipeline and the models it makes.",
    size: "compact",
  },
  {
    id: "board",
    title: "Board of Directors",
    aside:
      "The board leads the charge behind the scenes, helping the executive leadership team with direction and new initiatives.",
    size: "compact",
  },
];

export default function Team({ loaderData }) {
  const { people } = loaderData;
  const places = SITE.offices.map((o) => o.region || o.country).join(" and ");
  return (
    <>
      <section className={styles.hero} aria-labelledby="team-title">
        <div className="container">
          <Breadcrumbs items={CRUMBS} />
          <h1 id="team-title" className={styles.heroTitle}>
            The people behind the models
          </h1>
          <p className={styles.heroLead}>
            Engineers, developers and 3D modelers in the US and Israel, turning DOT design files and
            LiDAR scans into models and safety audits people can use.
          </p>
          <ul className={styles.figures} aria-label="The company in figures">
            <li>
              <strong>{people.length}</strong> people
            </li>
            <li>
              <strong>{SITE.foundedYear}</strong> founded
            </li>
            <li>
              <strong>{SITE.offices.length}</strong> headquarters, in {places}
            </li>
          </ul>
        </div>
      </section>

      {GROUPS.map((group) => {
        const members = people.filter((p) => p.group.includes(group.id));
        if (!members.length) return null;
        return (
          <section
            key={group.id}
            id={group.id}
            className="section"
            data-surface={surface(group.id)}
            aria-labelledby={`${group.id}-title`}
          >
            <div className="container">
              <SectionHeader
                id={`${group.id}-title`}
                title={group.title}
                aside={group.aside}
                link={group.link}
              />
              <div className={`${styles.people} ${styles[`${group.size}Grid`]}`}>
                {members.map((p) => (
                  <PersonCard key={p.slug} person={p} size={group.size} />
                ))}
              </div>
            </div>
          </section>
        );
      })}

      <CTABand
        variant="statement"
        image="/images/case-studies/i26-sc27-interchange/hero"
        title="Work with the team"
        text="Tell us about your corridor or your design, and we'll put the right people on it."
        secondary={{ to: "/contact", label: "Contact us" }}
      />
    </>
  );
}
